import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MessageSquare, Search, Clock, Calendar, MapPin, User, Send, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MessagesPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { formatDate, formatTime } = useSettings();
  
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('unread'); // 'all', 'unread'
  
  // Dialog state
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchBookings();
  }, []);

  // Check if we should open a specific booking's messages
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (bookingId && bookings.length > 0) {
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        openConversation(booking);
      }
    }
  }, [searchParams, bookings]);

  const fetchMessages = async () => {
    try {
      const [unreadRes, allRes] = await Promise.all([
        axios.get(`${API}/messages/unread`),
        axios.get(`${API}/messages/all`)
      ]);
      setMessages(unreadRes.data || []);
      setAllMessages(allRes.data || []);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const openConversation = async (booking) => {
    setSelectedBooking(booking);
    setShowMessageDialog(true);
    setLoadingConversation(true);
    
    try {
      const response = await axios.get(`${API}/messages/booking/${booking.id}`);
      setConversationMessages(response.data || []);
      
      // Mark messages as read
      await axios.post(`${API}/messages/booking/${booking.id}/mark-read`);
      
      // Refresh unread count
      fetchMessages();
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      toast.error('Failed to load conversation');
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedBooking) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/messages`, {
        booking_id: selectedBooking.id,
        content: newMessage,
        sender: 'dj'
      });
      
      setNewMessage('');
      
      // Refresh conversation
      const response = await axios.get(`${API}/messages/booking/${selectedBooking.id}`);
      setConversationMessages(response.data || []);
      
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr);
  };

  // Get messages to display based on filter
  const displayMessages = filter === 'unread' ? messages : allMessages;
  
  // Filter by search query
  const filteredMessages = displayMessages.filter(msg => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      msg.client_name?.toLowerCase().includes(query) ||
      msg.content?.toLowerCase().includes(query) ||
      msg.event_type?.toLowerCase().includes(query)
    );
  });

  // Group messages by booking
  const groupedMessages = filteredMessages.reduce((acc, msg) => {
    const key = msg.booking_id;
    if (!acc[key]) {
      acc[key] = {
        booking_id: msg.booking_id,
        client_name: msg.client_name,
        event_type: msg.event_type,
        event_date: msg.event_date,
        venue: msg.venue,
        messages: [],
        latestMessage: null,
        unreadCount: 0
      };
    }
    acc[key].messages.push(msg);
    if (!acc[key].latestMessage || new Date(msg.created_at) > new Date(acc[key].latestMessage.created_at)) {
      acc[key].latestMessage = msg;
    }
    if (!msg.is_read && msg.sender === 'client') {
      acc[key].unreadCount++;
    }
    return acc;
  }, {});

  const conversationGroups = Object.values(groupedMessages).sort((a, b) => 
    new Date(b.latestMessage?.created_at || 0) - new Date(a.latestMessage?.created_at || 0)
  );

  const unreadCount = messages.length;

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div data-testid="messages-page">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="messages-heading">
                Messages
                {unreadCount > 0 && (
                  <span className="ml-3 px-3 py-1 text-sm bg-red-500 text-white rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </h1>
              <p className="text-muted-foreground">Client communications from all bookings</p>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted/20 border-glass-border"
                data-testid="search-messages-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={filter === 'unread' ? 'default' : 'outline'}
                onClick={() => setFilter('unread')}
                className={filter === 'unread' ? 'bg-neon-blue hover:bg-neon-blue/90' : 'border-glass-border'}
                data-testid="filter-unread"
              >
                Unread ({messages.length})
              </Button>
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-neon-blue hover:bg-neon-blue/90' : 'border-glass-border'}
                data-testid="filter-all"
              >
                All Messages
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading messages...</div>
          ) : conversationGroups.length === 0 ? (
            <Card className="glass-card border-glass-border">
              <CardContent className="py-16 text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-xl font-semibold mb-2">No messages</h3>
                <p className="text-muted-foreground">
                  {filter === 'unread' 
                    ? "You're all caught up! No unread messages."
                    : "No messages yet. Client messages will appear here."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {conversationGroups.map((group) => {
                const booking = bookings.find(b => b.id === group.booking_id);
                return (
                  <Card 
                    key={group.booking_id} 
                    className="glass-card border-glass-border hover:border-neon-blue/50 transition-colors cursor-pointer"
                    onClick={() => booking && openConversation(booking)}
                    data-testid={`conversation-${group.booking_id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-neon-purple/20 flex items-center justify-center flex-shrink-0">
                          <User className="w-6 h-6 text-neon-purple" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <h3 className="font-semibold text-lg">{group.client_name || 'Client'}</h3>
                              {group.unreadCount > 0 && (
                                <span className="px-2 py-0.5 text-xs bg-red-500 text-white rounded-full">
                                  {group.unreadCount} new
                                </span>
                              )}
                            </div>
                            <span className="text-sm text-muted-foreground flex-shrink-0">
                              {group.latestMessage && formatTimeAgo(group.latestMessage.created_at)}
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {group.event_type}
                            </span>
                            {group.event_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {formatDate(group.event_date)}
                              </span>
                            )}
                            {group.venue && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {group.venue}
                              </span>
                            )}
                          </div>
                          
                          {group.latestMessage && (
                            <p className="mt-3 text-muted-foreground line-clamp-2">
                              <span className={group.latestMessage.sender === 'dj' ? 'text-neon-blue' : ''}>
                                {group.latestMessage.sender === 'dj' ? 'You: ' : ''}
                              </span>
                              {group.latestMessage.content}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/bookings?open=${group.booking_id}&tab=messages`);
                            }}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`view-booking-${group.booking_id}`}
                          >
                            View Booking
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent className="bg-card border border-border max-w-2xl max-h-[80vh] flex flex-col" data-testid="message-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-neon-purple" />
              {selectedBooking?.client_name}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedBooking?.event_type} • {selectedBooking && formatDate(selectedBooking.event_date)} • {selectedBooking?.venue}
            </p>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-[300px] py-4 space-y-4">
            {loadingConversation ? (
              <div className="text-center py-8 text-muted-foreground">Loading conversation...</div>
            ) : conversationMessages.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No messages yet. Start the conversation!</p>
              </div>
            ) : (
              conversationMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'dj' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                      msg.sender === 'dj'
                        ? 'bg-neon-blue/20 text-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                    data-testid={`message-${msg.id}`}
                  >
                    <p className="text-sm">{msg.content}</p>
                    <p className={`text-xs mt-1 ${msg.sender === 'dj' ? 'text-neon-blue/70' : 'text-muted-foreground'}`}>
                      {formatTimeAgo(msg.created_at)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="flex gap-2 pt-4 border-t border-glass-border">
            <Input
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              className="flex-1 bg-muted/20 border-glass-border"
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              className="bg-neon-blue hover:bg-neon-blue/90"
              data-testid="send-message-btn"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowMessageDialog(false);
                navigate(`/bookings?open=${selectedBooking?.id}&tab=messages`);
              }}
              className="w-full border-glass-border"
              data-testid="go-to-booking-btn"
            >
              Open Full Event Details
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
